// src/pages/ClientPayments/ClientPayments.jsx

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  FaWallet,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaCalendar,
  FaMoneyBillWave,
  FaUserCircle,
  FaProjectDiagram,
  FaDownload,
  FaEye,
  FaSearch,
  FaReceipt,
  FaEnvelope,
  FaPrint,
} from 'react-icons/fa';
import FurnitureNavbar from '../../../components/layout/Nav';
import './clientpayments.scss';
import withAuth from '../../auth/WithAuth';

const ClientPayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await serverUrlAuth.get('payments/client-payments/');
      setPayments(response.data);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments');
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <FaCheckCircle className="status-icon success" />;
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'failed':
        return <FaTimesCircle className="status-icon failed" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    return status?.toLowerCase() || 'pending';
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'refunded':
        return 'Refunded';
      case 'abandoned':
        return 'Abandoned';
      default:
        return status || 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return `KSh ${parseFloat(amount || 0).toLocaleString()}`;
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };
  // ✅ PDF generation with NO UI shrinking

  // src/pages/ClientPayments/ClientPayments.jsx

  const handleDownloadReceipt = async (paymentId) => {
    setDownloadingId(paymentId);
    setDownloadingPDF(true);

    try {
      toast.info('Generating PDF...');

      // ✅ Get the receipt HTML from the view endpoint
      const response = await serverUrlAuth.get(
        `payments/${paymentId}/receipt/view/`,
      );
      const receiptHtml = response.data;

      // Create a temporary container
      const container = document.createElement('div');
      container.innerHTML = receiptHtml;
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.width = '800px';
      container.style.padding = '40px';
      container.style.background = '#ffffff';
      container.style.zIndex = '-9999';
      document.body.appendChild(container);

      // Wait for images to load
      const images = container.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = resolve;
              img.onerror = resolve;
            }
          });
        }),
      );

      // Wait for rendering
      await new Promise((resolve) => setTimeout(resolve, 500));

      // ✅ Convert to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 800,
        logging: false,
      });

      // ✅ Create PDF
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({
        unit: 'px',
        format: [canvas.width, canvas.height],
        orientation: 'portrait',
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      pdf.save(`receipt-${paymentId}.pdf`);

      // Clean up
      document.body.removeChild(container);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
      setDownloadingId(null);
    }
  };

  const handleRetryPayment = (payment) => {
    navigate(`/projects`);
    toast.info('Please go to your project to retry payment');
  };

  // ✅ Sort payments: Pending first, then by date (newest first)
  const sortedPayments = [...payments].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (b.status === 'pending' && a.status !== 'pending') return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Filter payments
  const filteredPayments = sortedPayments.filter((payment) => {
    const matchesFilter =
      filter === 'all' || payment.status?.toLowerCase() === filter;
    const matchesSearch =
      payment.project_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.artisan_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Stats
  const stats = {
    total: payments.length,
    success: payments.filter((p) => p.status === 'success').length,
    pending: payments.filter((p) => p.status === 'pending').length,
    failed: payments.filter((p) => p.status === 'failed').length,
    totalAmount: payments.reduce(
      (sum, p) =>
        sum + (p.status === 'success' ? parseFloat(p.amount || 0) : 0),
      0,
    ),
  };

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="payments-loading">
          <div className="loading-spinner"></div>
          <p>Loading your payments...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <FurnitureNavbar />
      <div className="payments-page-modern">
        {/* Hero Section */}
        <section className="payments-hero-modern">
          <div className="hero-pattern"></div>
          <div className="hero-content-modern">
            <div className="welcome-badge">
              <FaWallet className="badge-icon" />
              <span>Payment History</span>
            </div>
            <h1 className="hero-title">
              <span className="gradient-text">My</span>
              <span className="hero-accent">Payments</span>
            </h1>
            <p className="hero-subtitle">
              Track all your payments and transactions in one place. View
              receipts, download invoices, and manage your payment history.
            </p>
          </div>
        </section>

        {/* Stats Dashboard */}
        <div className="stats-dashboard-modern">
          <div className="stat-card-modern">
            <div className="stat-icon-wrapper total">
              <FaWallet />
            </div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Payments</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon-wrapper success">
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>{stats.success}</h3>
              <p>Successful</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon-wrapper pending">
              <FaClock />
            </div>
            <div className="stat-content">
              <h3>{stats.pending}</h3>
              <p>Pending</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon-wrapper total-amount">
              <FaMoneyBillWave />
            </div>
            <div className="stat-content">
              <h3>{formatCurrency(stats.totalAmount)}</h3>
              <p>Total Paid</p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="payments-controls-modern">
          <div className="search-bar-modern">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by project, artisan, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
          <div className="filter-tabs-modern">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-tab ${filter === 'success' ? 'active' : ''}`}
              onClick={() => setFilter('success')}
            >
              ✅ Successful
            </button>
            <button
              className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              ⏳ Pending
            </button>
            <button
              className={`filter-tab ${filter === 'failed' ? 'active' : ''}`}
              onClick={() => setFilter('failed')}
            >
              ❌ Failed
            </button>
          </div>
          <button className="refresh-btn-modern" onClick={fetchPayments}>
            <FaSpinner className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>

        {/* ✅ Payments Grid - 3 columns on desktop */}
        <div className="payments-grid-modern">
          {error ? (
            <div className="error-state-modern full-width">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button onClick={fetchPayments} className="retry-btn-modern">
                Try Again
              </button>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="empty-state-modern full-width">
              <div className="empty-icon">💳</div>
              <h3>No payments found</h3>
              <p>
                {searchTerm || filter !== 'all'
                  ? 'No payments match your search criteria'
                  : "You haven't made any payments yet"}
              </p>
              {!searchTerm && filter === 'all' && (
                <Link to="/my-requests" className="btn-primary-custom">
                  View Your Projects
                </Link>
              )}
            </div>
          ) : (
            filteredPayments.map((payment) => (
              <div key={payment.id} className="payment-card-modern">
                <div className="payment-card-header">
                  <div className="payment-type-section">
                    <span className={`type-badge ${payment.payment_type}`}>
                      {payment.payment_type === 'deposit'
                        ? '💰 Deposit'
                        : '💳 Full Payment'}
                    </span>
                    <span
                      className={`status-badge ${getStatusClass(
                        payment.status,
                      )}`}
                    >
                      {getStatusIcon(payment.status)}
                      {getStatusLabel(payment.status)}
                    </span>
                  </div>
                  <div className="payment-reference">
                    <span>Ref: {payment.reference}</span>
                  </div>
                </div>

                <div className="payment-card-body">
                  <div className="payment-project-info">
                    <div className="project-icon-wrapper">
                      <FaProjectDiagram />
                    </div>
                    <div className="project-details">
                      <h4>{payment.project_title || 'Untitled Project'}</h4>
                      <p className="artisan-name">
                        <FaUserCircle />
                        {payment.client_name}
                      </p>
                    </div>
                  </div>
                  <div className="payment-amount-info">
                    <span className="amount">
                      {formatCurrency(payment.amount)}
                    </span>
                    <span className="date">
                      <FaCalendar />
                      {formatDate(payment.created_at)}
                    </span>
                  </div>
                </div>

                <div className="payment-card-footer">
                  <button
                    className="view-details-btn"
                    onClick={() => handleViewDetails(payment)}
                  >
                    <FaEye /> View Details
                  </button>

                  {payment.status === 'success' && (
                    <>
                      <button
                        className="download-btn"
                        onClick={() => handleDownloadReceipt(payment.id)}
                        disabled={
                          downloadingPDF && downloadingId === payment.id
                        }
                      >
                        <FaDownload />{' '}
                        {downloadingPDF && downloadingId === payment.id
                          ? 'Generating...'
                          : 'Download Receipt'}
                      </button>
                    </>
                  )}

                  {payment.status === 'pending' && (
                    <button
                      className="retry-btn"
                      onClick={() => handleRetryPayment(payment)}
                    >
                      Retry Payment
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Payment Detail Modal */}
        {showDetailModal && selectedPayment && (
          <div
            className="modal-overlay-modern"
            onClick={() => setShowDetailModal(false)}
          >
            <div
              className="modal-content-modern"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header-modern">
                <div className="modal-title-section">
                  <h2>Payment Details</h2>
                  <span
                    className={`status-badge ${getStatusClass(
                      selectedPayment.status,
                    )}`}
                  >
                    {getStatusLabel(selectedPayment.status)}
                  </span>
                </div>
                <button
                  className="close-btn-modern"
                  onClick={() => setShowDetailModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body-modern">
                <div className="detail-summary-card">
                  <div className="detail-amount">
                    <span className="label">Total Amount</span>
                    <span className="value">
                      {formatCurrency(selectedPayment.amount)}
                    </span>
                  </div>
                  <div className="detail-payment-type">
                    <span className="label">Payment Type</span>
                    <span
                      className={`type-badge ${selectedPayment.payment_type}`}
                    >
                      {selectedPayment.payment_type === 'deposit'
                        ? '💰 Deposit (50%)'
                        : '💳 Full Payment'}
                    </span>
                  </div>
                </div>

                <div className="detail-grid-modern">
                  <div className="detail-item-modern">
                    <label>Reference</label>
                    <p>{selectedPayment.reference}</p>
                  </div>
                  {/* <div className="detail-item-modern">
                    <label>Transaction ID</label>
                    <p>{selectedPayment.paystack_reference || 'N/A'}</p>
                  </div> */}
                  <div className="detail-item-modern">
                    <label>Payment Date</label>
                    <p>{formatDate(selectedPayment.created_at)}</p>
                  </div>
                  <div className="detail-item-modern">
                    <label>Completed Date</label>
                    <p>
                      {selectedPayment.paid_at
                        ? formatDate(selectedPayment.paid_at)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="detail-item-modern full-width">
                    <label>Project</label>
                    <p className="project-name">
                      {selectedPayment.project_title}
                    </p>
                  </div>
                  <div className="detail-item-modern full-width">
                    <label>Artisan</label>
                    <p className="artisan-name-detail">
                      <FaUserCircle />
                      {selectedPayment.artisan_name || 'N/A'}
                    </p>
                  </div>
                  {selectedPayment.receipt_number && (
                    <div className="detail-item-modern full-width">
                      <label>Receipt Number</label>
                      <p className="receipt-number">
                        {selectedPayment.receipt_number}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer-modern">
                {selectedPayment.status === 'success' && (
                  <button
                    className="download-btn"
                    onClick={() => handleDownloadReceipt(selectedPayment.id)}
                    disabled={
                      downloadingPDF && downloadingId === selectedPayment.id
                    }
                  >
                    <FaDownload />{' '}
                    {downloadingPDF && downloadingId === selectedPayment.id
                      ? 'Generating...'
                      : 'PDF'}
                  </button>
                )}
                <button
                  className="close-modal-btn-modern"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default withAuth(ClientPayments, 'client');
